import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useLatestValueRef } from '../hooks/use-latest-value-ref';

import { isFirefox } from './ua';

type SpeechRecognitionContextType = {
    transcript: string;
    activeRecognId: string | null;
    onEnable: (recognId: string) => void;
    onDisable: (recognId: string) => void;
    onReset: () => void;
};

const SpeechRecognitionContext = createContext({} as SpeechRecognitionContextType);

export const SpeechRecognitionContextProvider: FC<PropsWithChildren<any>> = ({ children }) => {
    const [activeRecognId, setActiveRecognId] = useState<string | null>(null);
    const activeRecognIdRef = useLatestValueRef(activeRecognId);
    const [transcript, setTranscript] = useState('');
    const timeoutRef = useRef<null | number>(null);
    const { resetTranscript, transcript: transcriptRaw } = useSpeechRecognition({
        clearTranscriptOnListen: true,
    });

    useEffect(() => {
        if (!activeRecognIdRef.current) return;
        const timeoutId = window.setTimeout(() => {
            setTranscript(transcriptRaw);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [transcriptRaw]);

    const start = useCallback(() => {
        SpeechRecognition.startListening({
            continuous: true,
            language: 'ru-RU',
        });
    }, []);

    const stop = useCallback(() => {
        SpeechRecognition.abortListening();
        SpeechRecognition.stopListening();
        const recog = SpeechRecognition.getRecognition();
        if (recog) {
            recog.stop();
        }
        setTimeout(() => {
            resetTranscript();
            setTranscript('');
        }, 100);
    }, [resetTranscript]);

    const close = useCallback(() => {
        stop();
        setActiveRecognId(null);
    }, [stop]);

    useEffect(() => {
        if (transcript) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(close, 10_000);
        }
    }, [transcript, close]);

    const onEnable = useCallback(
        (recognId: string) => {
            if (activeRecognIdRef.current && activeRecognIdRef.current !== recognId) {
                stop();
            }

            start();
            setActiveRecognId(recognId);
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(close, 10_000);
        },
        [start, stop, close],
    );

    const onDisable = useCallback(
        (recognId: string) => {
            stop();
            setActiveRecognId((prevRecognId) => (prevRecognId === recognId ? null : prevRecognId));
        },
        [stop],
    );

    return (
        <SpeechRecognitionContext.Provider
            value={useMemo(
                () => ({ activeRecognId, transcript, onDisable, onEnable, onReset: close }),
                [transcript, activeRecognId, onDisable, onEnable, close],
            )}
        >
            {children}
        </SpeechRecognitionContext.Provider>
    );
};

export const useSpeechRecognCtx = (recordId: string, onChange: (value: string) => void) => {
    const { activeRecognId, onDisable, onEnable, transcript } = useContext(SpeechRecognitionContext);
    const activeRecognIdRef = useLatestValueRef(activeRecognId);
    const isRecogniting = activeRecognId === recordId;

    const { browserSupportsSpeechRecognition } = useSpeechRecognition({
        clearTranscriptOnListen: true,
    });

    useEffect(() => {
        if (isRecogniting) {
            if (transcript) {
                onChange(transcript);
            }
        }
    }, [isRecogniting, transcript, onChange]);

    useEffect(
        () => () => {
            onDisable(recordId);
        },
        [onDisable, recordId],
    );

    return {
        isActive: isRecogniting,
        enabled: browserSupportsSpeechRecognition && !isFirefox,
        onClick: useCallback(() => {
            if (activeRecognIdRef.current === recordId) {
                onDisable(recordId);
            } else {
                onEnable(recordId);
            }
        }, [recordId, onDisable, onEnable]),
    };
};

export const useResetSpeechRecognCtx = () => useContext(SpeechRecognitionContext).onReset;
